import {
  ChangeDetectionStrategy,
  Component,
  signal,
  Input,
  Inject,
  AfterViewInit,
  EventEmitter,
  Output,
} from '@angular/core';
import { PVColumn } from '../../shared/column.model';
import { TenantSummaryService } from './tenant-summary.service';
import { TenantSpace, TenantSpaceFormatted } from './tenant-summary.model';
import { TENANT_SUMMARY_COLUMNS } from './tenant-summary.const';
import { DefaultTableSelectionHandler, TABLE_SELECTOR_HANDLER } from '@cbre/components';
import { calculateFootPrint, formatTenantSummary } from './tenant-summary.utils';
import {
  FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN,
  FloorSpaceDetailsHandlerSrv,
} from '../floor-space-details-state-handler.service';
import { StackPlanViewStateService } from '../../../state/sp-view-state.service';
import { groupByDisabledUsages } from '../group-usages.const';

@Component({
  selector: 'pv-tenant-summary',
  template: `
    <div class="pv-tenant-summary__wrapper">
      <div class="pvts-title-wrapper">
        <!-- Back button -->
        <div class="pvts-title-back-button-container ">
          <mat-icon class="pvts-title-icon pvts-title--dark-green" (click)="onClickBack()">arrow_back</mat-icon>
          {{ 'pv.tenantSummary.backButton' | xTranslate }}
        </div>

        <!-- FootPrint -->
        <div class="pvts-title-footprint">
          <span class="pvts-title--medium-grey">{{
            'pv.tenantSummary.totalFootprintArea' | xTranslate: { usage: currentUsage }
          }}</span
          >&nbsp;<span class="pvts-title--dark-green">{{ $footPrint() | number: '.0-0' }} SF</span>
        </div>
      </div>

      <div *ngIf="$tenantData() as records" class="pvts-table-wrapper">
        <cbre-table
          tableId="pvFloorSpaceDetails"
          [elements]="records"
          [columns]="$columns()"
          [hidePaginator]="true"
          [cellRendererTpl]="cellTpl"
          [stickyHeader]="true"
          [totalCount]="records.length"
          class="pvts-table"
        ></cbre-table>
      </div>

      <ng-template #cellTpl let-row="row" let-column="column">{{ row | dataColumnFormatter: column }}</ng-template>
    </div>
  `,
  styleUrls: ['./tenant-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: TABLE_SELECTOR_HANDLER, useClass: DefaultTableSelectionHandler }],
  standalone: false,
})
export class PVTenantSummaryComponent implements AfterViewInit {
  currentUsage: string;
  @Input() set tenantIds(idList: number[]) {
    if (Array.isArray(idList)) {
      this.$tenantData.set(this.#getTenantData(idList));
      this.$footPrint.set(this.#getFootPrint(idList));
    }
  }

  @Output() afterViewInit = new EventEmitter<boolean>();

  $footPrint = signal<number>(null);
  $columns = signal<PVColumn[]>([]);
  $tenantData = signal<TenantSpaceFormatted[]>(null);

  #transFormData = (tenantSpaces: TenantSpace[]) => {
    const transformedData = tenantSpaces
      .map(space => formatTenantSummary(space, this.currentUsage))
      .sort((s1, s2) => this.initialSortFn(s1, s2));
    return groupByDisabledUsages(transformedData) as TenantSpaceFormatted[];
  };

  /**
   * Sort function for the initial sort of the table
   * - Sort by active property type first
   * - Sort by property type
   * - Sort by floor desc
   * (MIQ-28824)
   */
  initialSortFn = (fs1: TenantSpaceFormatted, fs2: TenantSpaceFormatted) => {
    if (fs1.isSelectedPropertyType === fs2.isSelectedPropertyType) {
      if (fs1.propertyType === fs2.propertyType) {
        if (fs1.floor === fs2.floor) {
          return 0;
        }
        // sort by Floor desc
        return (Number(fs1.floor) - Number(fs2.floor)) * -1;
      }
      //sort by property type
      return fs1.propertyType.localeCompare(fs2.propertyType);
    }
    // sort by active property type first
    return fs1.isSelectedPropertyType ? -1 : 1;
  };

  #removeDuplicatedSpaces = (tenantSpaces: TenantSpace[]) => {
    const seen = new Set();
    return tenantSpaces.filter(space => {
      const duplicate = seen.has(space.space_id);
      seen.add(space.space_id);
      return !duplicate;
    });
  };

  #sortData = (tenantSpaces: TenantSpace[]) => tenantSpaces.sort((a, b) => b.floor_index - a.floor_index);

  #getFootPrint = (tenantIds: number[]) => {
    const floors = this.tenantSummary.getFloorsSummary();
    return calculateFootPrint(tenantIds, floors, this.currentUsage);
  };

  #getTenantData = (tenantIdList: number[]) => {
    const tenantsData = tenantIdList.map(id => this.tenantSummary.getTenantSummaryById(id)?.spaces ?? []).flat();
    return this.#transFormData(this.#sortData(this.#removeDuplicatedSpaces(tenantsData)));
  };

  constructor(
    private tenantSummary: TenantSummaryService,
    private spViewState: StackPlanViewStateService,
    @Inject(FLOOR_SPACE_DETAILS_STATE_HANDLER_TOKEN) private stateHandler: FloorSpaceDetailsHandlerSrv
  ) {
    this.$columns.set(TENANT_SUMMARY_COLUMNS.map(c => ({ ...c })));
    this.currentUsage = this.spViewState.getState().propertyType ?? '';
  }

  ngAfterViewInit(): void {
    this.afterViewInit.emit(true);
  }

  onClickBack() {
    this.stateHandler.nextState(null);
  }
}
